import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, Form, notification, InputNumber } from 'antd';
import ParameterService from '../../../../domain/Parameter/ParameterService';
import { useNavigate } from 'react-router-dom';
import { getModelFiles } from '../../../../services/model';
import InpService from '../../../../domain/ModelFile/InpService';
import SelectInpFile from '@/basicsComponents/Selects/SelectInpFile';
import ParameterSourceService from '../../../../domain/ParameterSource/ParameterSourceService';
import InfraestructureContext from '@/contexts/InfraestructureContext';
import SelectMetereological from '@/basicsComponents/Selects/SelectMetereological';
import { Infrastructure } from '@/domain/Infrastructure/Infrastructure';
import { selectUnitsByTypeId, getTypesWithUnitsAction } from '@/redux/slices/measurementTypesWithUnitsSlice';
import { infraTypes } from '../../../../statics/constants/infraTypes';
import SelectAsset from '@/basicsComponents/Selects/SelectAsset';
import { Button } from '@hydrodigital/design-system';

type LayoutType = Parameters<typeof Form>[0]['layout'];

const parameterService = new ParameterService();
const inpService = new InpService();
const parameterSourceService = new ParameterSourceService();

export interface ISourcesDetailProps {
	infrastructure: Infrastructure | null;
	onClose: Function;
}

const MM_H_UNIT_ID = 25;

export default function Forecast({ infrastructure, onClose }: ISourcesDetailProps) {
	const security = localStorage.getItem('security');
	const organizationId = JSON.parse(security || '{}').organizationId;
	const { value: context, setValue: setContext } = useContext(InfraestructureContext);
	const dispatch = useDispatch();
	const typesWithUnits = useSelector((state: any) => state.typesWithUnits.array);
	const [formLayout, setFormLayout] = useState<LayoutType>('horizontal');
	const [form] = Form.useForm();
	const selectAssetRef = useRef<any>(null);
	const [filterUnitsByType, setFilterUnitsByType] = useState<any[]>([]);
	const savedForecastUnitId = context.selectedParam?.parameterForecastUnitId ?? null;
	const savedForecastUnitName = context.selectedParam?.parameterForecastUnitName ?? null;
	const previousParamTypeId = context.selectedParam?.previousParamTypeId ?? null;
	const previousParamUnitId = context.selectedParam?.previousParamUnitId ?? null;
	const previousParamUnitName = context.selectedParam?.previousParamUnitName ?? null;
	const previousEffectiveForecastUnitId =
		context.selectedParam?.previousEffectiveForecastUnitId ?? null;
	const previousEffectiveForecastUnitName =
		context.selectedParam?.previousEffectiveForecastUnitName ?? null;
	const effectiveForecastUnitId =
		context.selectedParam?.effectiveForecastUnitId ?? null;
	const effectiveForecastUnitName =
		context.selectedParam?.effectiveForecastUnitName ?? null;
	const currentParameterTypeId = context.selectedParam?.paramTypeId ?? null;
	const defaultParameterUnitId = context.selectedParam?.paramUnitId ?? null;
	const defaultParameterUnitName = context.selectedParam?.parameterUnit ?? null;
	const isSavedForecastCustom = context.selectedParam?.parameterForecastUnitIsCustom || false;
	const savedForecastUnitBelongsToCurrentType =
		savedForecastUnitId != null &&
		currentParameterTypeId != null &&
		typesWithUnits.some(
			(type: any) =>
				`${type.id}` === `${currentParameterTypeId}` &&
				type.units?.some((unit: any) => `${unit.id}` === `${savedForecastUnitId}`),
		);
	const shouldSyncForecastUnitWithParameter =
		!isSavedForecastCustom &&
		previousParamTypeId != null &&
		currentParameterTypeId != null &&
		`${previousParamTypeId}` !== `${currentParameterTypeId}` &&
		previousParamUnitId != null &&
		defaultParameterUnitId != null &&
		`${previousParamUnitId}` !== `${defaultParameterUnitId}`;
	const currentForecastUnitId =
		isSavedForecastCustom
			? null
			: shouldSyncForecastUnitWithParameter
				? defaultParameterUnitId
			: savedForecastUnitId != null && savedForecastUnitBelongsToCurrentType
				? savedForecastUnitId
				: (effectiveForecastUnitId ??
					previousEffectiveForecastUnitId ??
					previousParamUnitId ??
					defaultParameterUnitId);
	const currentForecastUnitName =
		isSavedForecastCustom
			? 'Custom'
			: shouldSyncForecastUnitWithParameter
				? defaultParameterUnitName || `Unit ${currentForecastUnitId}`
			: savedForecastUnitId != null && savedForecastUnitBelongsToCurrentType
				? (savedForecastUnitName || defaultParameterUnitName || `Unit ${currentForecastUnitId}`)
				: (effectiveForecastUnitName ||
					previousEffectiveForecastUnitName ||
					previousParamUnitName ||
					defaultParameterUnitName ||
					`Unit ${currentForecastUnitId}`);
	const currentForecastUnitInList = filterUnitsByType.some((u: any) => u.id === currentForecastUnitId);
	const forecastFallbackUnit =
		!shouldSyncForecastUnitWithParameter && !currentForecastUnitInList && currentForecastUnitId != null
			? { id: currentForecastUnitId, name: currentForecastUnitName }
			: null;
	const [isUnitCustom, setIsUnitCustom] = useState<boolean>(false);
	const [inpId, setInpId] = useState<any>(
		context.selectedParam?.inpId != undefined && context.selectedParam?.inpId > 0
			? context.selectedParam?.inpId
			: null,
	);
	const [inpObjectId, setInpObjectId] = useState<any>(
		context.selectedParam ? context.selectedParam?.inpObjectId : null,
	);
	const [inpObjectType, setInpObjectType] = useState<any>(
		context.selectedParam?.inpObjectType != undefined && context.selectedParam?.inpObjectType > 0
			? context.selectedParam?.inpObjectType
			: null,
	);
	const [modelInfraIdList, setModelInfraIdList] = useState<any[]>([]);

	const [pagination, setPagination] = useState({
		page: 1,
		pageSize: 10,
		totalRows: 0,
	});
	const [modelFiles, setModelFiles] = useState<any>([]);

	const handleGetModelInfraId = async () => {
		if (form.getFieldValue('inpId') && form.getFieldValue('inpObjectType')) {
			const response = await inpService.getModelInfrastructureId({
				inpId: form.getFieldValue('inpId'),
				typeId: form.getFieldValue('inpObjectType'),
			});
			setModelInfraIdList(response.data);
		}
	};

	const navigate = useNavigate();


	const uploadForecastData = async (values: any) => {
		const selectedForecastUnitName =
			filterUnitsByType.find((u) => u.id === values.forecastUnitId)?.name ??
			(values.forecastUnitId === currentForecastUnitId ? currentForecastUnitName : null);
		const forecast = {
			inpId: parseInt(values.inpId),
			inpObjectId: values.inpObjectId,
			inpObjectType: parseInt(values.inpObjectType),
			unitId: isUnitCustom ? null : (values.forecastUnitId ?? null),
			unitName: isUnitCustom
				? 'Custom'
				: selectedForecastUnitName,
			unitScale: isUnitCustom ? (values.forecastUnitScale ?? 1) : null,
			unitOffset: isUnitCustom ? (values.forecastUnitOffset ?? 0) : null,
			unitIsCustom: isUnitCustom,
			unitTypeId: context.selectedParam?.paramTypeId ?? null,
		};

		if (context.selectedParam?.id) {
			const resp = await parameterService.postParameterForecast(
				context.selectedParam?.id,
				forecast,
			);

			if (resp) {
				notification.success({
					message: 'Successful',
					description: 'All files have been saved',
				});
				onClose();
			}
		}
	};

	const handleUpdateModelFiles = async () => {
		let organizationId = [];
		const printItems = localStorage.getItem('security');
		if (printItems) {
			organizationId = JSON.parse(printItems).organizationId;
		}
		getModelFiles(organizationId, {
			page: pagination.page,
			size: pagination.pageSize,
			locationId: infrastructure?.locationId,
		})
			.then((response) => {
				setPagination({
					...pagination,
					totalRows: response.totalRecords,
				});
				setModelFiles(response.getAllResponseDTOList);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const onFinish = async (values: any) => {
		await uploadForecastData(values);
	};

	const handleSaveSources = async (sourceListArray: any) => {
		if (context.selectedParam && infrastructure) {
			try {
				await parameterSourceService.saveSourcesByInfrastructure(
					infrastructure,
					context.selectedParam,
					sourceListArray,
				);
				notification.success({
					message: 'Successful',
					description: 'Sources saved successfully',
				});
			} catch (error) {
				notification.error({
					message: 'Error',
					description: 'Error saving sources',
				});
			}
		}
	};

	useEffect(() => {
		if (typesWithUnits.length === 0) {
			dispatch(getTypesWithUnitsAction() as any);
		}
	}, []);

	useEffect(() => {
		if (context.selectedParam?.paramTypeId && typesWithUnits.length > 0) {
			const units = selectUnitsByTypeId(typesWithUnits, context.selectedParam.paramTypeId);
			setFilterUnitsByType(units);
			
		}
	}, [context.selectedParam?.paramTypeId, typesWithUnits]);

	useEffect(() => {
		if (context.selectedParam?.id) {
			const isForecastCustom = context.selectedParam.parameterForecastUnitIsCustom || false;
			
			form.setFieldsValue({
				inpId: context.selectedParam.inpId || null,
				inpObjectType: context.selectedParam.inpObjectType || null,
				inpObjectId: context.selectedParam.inpObjectId || null,
				forecastUnitId: isForecastCustom
					? 'custom'
					: (currentForecastUnitId ?? null),
				forecastUnitScale: isForecastCustom ? (context.selectedParam.parameterForecastUnitScale ?? 1) : null,
				forecastUnitOffset: isForecastCustom ? (context.selectedParam.parameterForecastUnitOffset ?? 0) : null,
			});
			setIsUnitCustom(isForecastCustom);
		}
	}, [context.selectedParam, currentForecastUnitId, currentForecastUnitName, currentParameterTypeId, defaultParameterUnitId, defaultParameterUnitName, filterUnitsByType, previousParamTypeId, previousParamUnitId, previousParamUnitName, savedForecastUnitId, savedForecastUnitName, shouldSyncForecastUnitWithParameter]);

	return infrastructure?.infraTypeId != 8 ? (
		<Form
			form={form}
			layout="vertical"
			autoComplete="off"
			size="small"
			onFinish={onFinish}
			onFinishFailed={() => {}}
		>
			<div className="flex column mt-2">
				<Form.Item label="Model File (id - name)" name="inpId" rules={[]}>
					<SelectInpFile
						defaultValue={inpId}
						onSelect={(val: any) => {
							form.setFieldValue('inpId', val);
							setInpId(val);
							// handleGetModelInfraId();
						}}
						organizationId={organizationId}
						onlyCompleted
						requireLocation
						locationId={infrastructure?.locationId}
					/>
				</Form.Item>

				<Form.Item label="Object Type" name="inpObjectType" rules={[]}>
					<Select
						style={{ width: '100%' }}
						defaultValue={inpObjectType}
						placeholder="Select an option"
						onChange={(val: any) => {
							form.setFieldValue('inpObjectType', val);
							setInpObjectType(val);
							form.setFieldValue('inpObjectId', null);
							setInpObjectId(null);
							selectAssetRef.current?.reset?.();
						}}
						options={[
							{ value: 1, label: 'Nodes' },
							{ value: 2, label: 'Links' },
						]}
					/>
				</Form.Item>
				<Form.Item label="Model Infrastructure ID" name="inpObjectId" rules={[]}>
					<SelectAsset
						ref={selectAssetRef}
						inpId={inpId}
						typeId={inpObjectType}
						defaultValue={inpObjectId}
						onSelect={(val: any) => {
							form.setFieldValue('inpObjectId', val);
							setInpObjectId(val);
						}}
					/>
				</Form.Item>
				<Form.Item label="Unit" name="forecastUnitId" rules={[]}>
					<Select
						style={{ width: '100%' }}
						placeholder="Select a unit"
						onChange={(value) => {
							const isCustom = value === 'custom';
							setIsUnitCustom(isCustom);
							form.setFieldValue('forecastUnitId', isCustom ? null : value);
							form.setFieldValue('forecastUnitIsCustom', isCustom);
							if (!isCustom) {
								form.setFieldValue('forecastUnitScale', null);
								form.setFieldValue('forecastUnitOffset', null);
							}
						}}
					>
						{forecastFallbackUnit && (
							<Select.Option key={forecastFallbackUnit.id} value={forecastFallbackUnit.id}>
								{forecastFallbackUnit.name}
							</Select.Option>
						)}
						{filterUnitsByType.map((unit: any) => (
							<Select.Option key={unit.id} value={unit.id}>
								{unit.name}
							</Select.Option>
						))}
						<Select.Option value="custom">Custom</Select.Option>
					</Select>
				</Form.Item>
				{isUnitCustom && (
					<div className="flex items-center gap-3 mb-3">
						<Form.Item label="Scale" name="forecastUnitScale" style={{ marginBottom: 0 }}>
							<InputNumber
								placeholder="1.0"
								defaultValue={context.selectedParam?.parameterForecastUnitScale ?? 1}
								style={{ width: '100%' }}
							/>
						</Form.Item>
						<Form.Item label="Offset" name="forecastUnitOffset" style={{ marginBottom: 0 }}>
							<InputNumber
								placeholder="0.0"
								defaultValue={context.selectedParam?.parameterForecastUnitOffset ?? 0}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</div>
				)}
				<div className="flex pt-3 justify-between items-center">
					<div className="flex" style={{ gap: '15px' }}>
						<Button
							label="Cancel"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#F7F9FA',
								color: '#434B56',
								border: '1px solid #C4CCD5',
							}}
							onClick={() => onClose()}
						/>

						<Button
							label="Reset"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#F7F9FA',
								color: '#434B56',
								border: '1px solid #C4CCD5',
							}}
							onClick={() => {
								form.resetFields();
								setInpId(null);
								setInpObjectId(null);
								setInpObjectType(null);
								setModelInfraIdList([]);
							}}
						/>
					</div>
					<Button
						label="Save"
						onClick={() => form.submit()}
						showIcon={false}
						style={{
							width: '85px',
							height: '40px',
							backgroundColor: '#00203C',
							color: 'white',
						}}
					/>
				</div>

			</div>
		</Form>
	) : context.selectedParam && context.selectedParam.sources.length > 0 ? (
		<div className="mt-2 flex column">
			<span className="mb-1">Meteorological Service</span>
			<SelectMetereological
				mode="forecast"
				defaultValue={
					context.selectedParam && context.selectedParam.sources?.length > 0
						? context.selectedParam?.sources[0].forecastMetService
						: null
				}
				onSelect={(value: number | null | undefined) => {
					const sourcesCopy = context.selectedParam?.sources
						? [...context.selectedParam.sources]
						: [];
					sourcesCopy.forEach((source) => {
						source.forecastMetService = value;
					});
					handleSaveSources(sourcesCopy);
				}}
			/>
			<div className="mt-2">
				<span className="mb-1">Unit</span>
				<Select
					style={{ width: '100%', marginTop: 4 }}
					value={MM_H_UNIT_ID}
					disabled
				>
					<Select.Option value={MM_H_UNIT_ID}>mm/h</Select.Option>
				</Select>
			</div>
		</div>
	) : (
		<div className="mt-2">
			<span>To select the service you must create at least one source</span>
		</div>
	);
}
